/**
 * Slideshow button position
 *
 * Lets the slideshow's buttons (e.g. "Shop now") be nudged vertically without
 * taking them out of the document flow, so the heading and text above them
 * stay exactly where the slide layout put them.
 *
 * The section sets `--slideshow-button-offset` (0 = natural position, 1 = the
 * very bottom of the slide). This script measures how much room is actually
 * left between each button and the bottom of its slide and publishes it as
 * `--slideshow-button-travel`, so the same percentage lands correctly on every
 * breakpoint, slide height and content length - mobile and desktop alike.
 *
 * Measurements use offsetTop / clientHeight rather than getBoundingClientRect
 * so they are unaffected by the transforms used by the slideshow itself.
 */
(() => {
  if (window.slideshowButtonPosition) return;

  const BUTTON_SELECTOR = '.slide__content .button-block';
  const SLIDE_SELECTOR = '.slide__content';
  const TRAVEL_PROPERTY = '--slideshow-button-travel';

  const buttons = new Set();
  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
  let frame = null;

  /**
   * Vertical layout position of `element` inside `root`, ignoring transforms.
   * Returns null when `root` is not part of the offset parent chain.
   */
  function layoutTop(element, root) {
    let top = 0;
    let node = element;

    while (node && node !== root) {
      top += node.offsetTop;
      node = node.offsetParent;
    }

    return node === root ? top : null;
  }

  function measure(button) {
    const slide = button.closest(SLIDE_SELECTOR);
    if (!slide) return;

    // Slides use `content-visibility: auto`, so off-screen slides report zero
    // sized descendants. Keep the last good value until they render for real.
    if (slide.clientHeight === 0 || button.offsetHeight === 0) return;

    const paddingBottom = parseFloat(getComputedStyle(slide).paddingBottom) || 0;
    const top = layoutTop(button, slide);
    let travel;

    if (top === null) {
      // Fallback: neutralise the offset first so the rects are comparable.
      button.style.setProperty(TRAVEL_PROPERTY, '0px');
      travel = slide.getBoundingClientRect().bottom - paddingBottom - button.getBoundingClientRect().bottom;
    } else {
      travel = slide.clientHeight - paddingBottom - top - button.offsetHeight;
    }

    button.style.setProperty(TRAVEL_PROPERTY, `${Math.max(0, Math.round(travel * 100) / 100)}px`);
  }

  function schedule() {
    if (frame) return;

    frame = requestAnimationFrame(() => {
      frame = null;

      buttons.forEach((button) => {
        if (button.isConnected) {
          measure(button);
        } else {
          buttons.delete(button);
          resizeObserver?.unobserve(button);
        }
      });
    });
  }

  function track(button) {
    if (buttons.has(button)) return;

    buttons.add(button);
    measure(button);

    if (resizeObserver) {
      resizeObserver.observe(button);
      const slide = button.closest(SLIDE_SELECTOR);
      if (slide) resizeObserver.observe(slide);
    }

    // Re-measure when the slideshow advances and a slide starts rendering.
    const slideElement = button.closest('slideshow-slide');
    if (slideElement && !slideElement.dataset.buttonPositionWatched) {
      slideElement.dataset.buttonPositionWatched = 'true';
      new MutationObserver(schedule).observe(slideElement, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'hidden'],
      });
    }
  }

  function scan(root) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    scope.querySelectorAll(BUTTON_SELECTOR).forEach(track);
  }

  window.slideshowButtonPosition = { scan, refresh: schedule };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  document.fonts?.ready.then(schedule);

  // Keep working while the merchant edits the section in the theme editor.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:block:select', 'shopify:block:deselect'].forEach(
    (eventName) => {
      document.addEventListener(eventName, (event) => {
        scan(event.target);
        schedule();
      });
    }
  );
})();